import { FC } from 'react';

import { MicrophoneIcon, TrashIcon } from '@heroicons/react/outline';

import Avatar from '../Avatar';
import Button from '../Button';
import IconButton from '../IconButton';
import TextAreaGroup from '../TextAreaGroup';
import ImageUploadButton, { MediaInfo } from './ImageUploadButton';

export interface ChatInputRowProps {
  avatarName?: string;
  avatarImageUrl?: string | null;
  placeholder: string;
  attachedImageMedia: MediaInfo | null;
  onAttachedImageMediaChange: (media: MediaInfo | null) => void;
  commentValue: string;
  commentRegisterRef: (el: HTMLTextAreaElement | null) => void;
  commentRegisterRest: Omit<
    React.ComponentProps<'textarea'>,
    'ref' | 'value' | 'onKeyDown'
  >;
  onCommentKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPostClick: () => void;
  isPostDisabled: boolean;
  showPostButton: boolean;
  onRecordVoiceNoteClick: () => void;
  autoFocus?: boolean;
}

const ChatInputRow: FC<ChatInputRowProps> = ({
  avatarName,
  avatarImageUrl,
  placeholder,
  attachedImageMedia,
  onAttachedImageMediaChange,
  commentValue,
  commentRegisterRef,
  commentRegisterRest,
  onCommentKeyDown,
  onPostClick,
  isPostDisabled,
  showPostButton,
  onRecordVoiceNoteClick,
  autoFocus = true,
}) => {
  return (
    <div className="flex w-full min-w-0 flex-row items-center gap-x-3 py-3">
      <Avatar
        size="medium"
        name={avatarName}
        imageUrl={avatarImageUrl}
        className="shrink-0"
      />

      <div className="min-h-12 group relative flex min-w-0 flex-1 flex-row items-end justify-between rounded-xl bg-neutral-50 focus-within:ring-2 focus-within:ring-secondary-100">
        <div className="flex min-w-0 flex-1 flex-col">
          {attachedImageMedia && (
            <div className="relative w-full p-4">
              <img
                className="max-h-[100px]"
                src={attachedImageMedia.url}
                alt="Attachment"
              />
              <TrashIcon
                className="absolute top-2 left-0 ml-2 h-6 w-6 cursor-pointer rounded-full bg-white p-1 text-red-100 hover:bg-red-100 hover:text-white"
                onClick={() => onAttachedImageMediaChange(null)}
              />
            </div>
          )}
          <TextAreaGroup
            label="Comment"
            placeholder={placeholder}
            labelHidden
            autoFocus={autoFocus}
            backgroundHidden
            rows={1}
            inputSize="small"
            containerClassName="min-w-0 w-full"
            className="w-full rounded-xl !border-transparent !text-caption"
            value={commentValue}
            {...commentRegisterRest}
            ref={commentRegisterRef}
            onKeyDown={onCommentKeyDown}
          />
        </div>
        {showPostButton && (
          <Button
            title="Post"
            theme="none"
            type="submit"
            size="small"
            className="h-12 pr-5 font-bold text-neutral-150 disabled:text-neutral-100"
            disabled={isPostDisabled}
            noBackground
            noOutline
            noFocus
            onClick={onPostClick}
          />
        )}
      </div>

      <div className="flex shrink-0 items-center gap-x-1">
        <ImageUploadButton onSuccess={onAttachedImageMediaChange} />
        {!showPostButton && (
          <IconButton
            aria-label="Record voice note"
            className="h-10 w-10 justify-center text-neutral-125"
            iconClassName="h-6 w-6 shrink-0 stroke-neutral-125"
            IconComponent={MicrophoneIcon}
            onClick={onRecordVoiceNoteClick}
          />
        )}
      </div>
    </div>
  );
};

export default ChatInputRow;
